import React, {
  useEffect,
  useRef,
  useState
} from 'react';

//Router dom
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

//Redux
import { useDispatch, useSelector } from 'react-redux';

// funciones
import { categoriesLoad } from '../../../../actions/category';
import { generateURL, getTitle } from '../../../../helpers/admin/productHelper';
import { form1Validator } from '../../../../validators/Admin/Product/ProductValidator';
import {
  addProduct,
  deleteProductDraftImage,
  getProductDraft,
  loadProductList,
  productSnackbarClose,
  publishProductDraft,
  saveProductDraft
} from '../../../../actions/product';
import { uiLoading2 } from '../../../../actions/ui';
import { msg as swalMsg, posMsg } from '../../../../actions/swal_msg';

//CSS
import './newproduct.css';

// componentes
import NewVariant from './NewVariant';
import ProductFBT from './FBT/ProductFBT';
import ProductBrand from './Brand/ProductBrand';
import CategorySelect from './Category/CategorySelect';
import ImagesContainer from '../Images/ImagesContainer';
import ProductKeywords from './Keywords/ProductKeywords';
import { useProductExitGuard } from './productExitGuard';

//MUI
import {
  TextField, Snackbar, IconButton, Button, Backdrop, CircularProgress,
  ToggleButton, Container, Typography, Grid, Box,
  FormGroup, FormControlLabel, Switch, Divider
} from '@mui/material';
// import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarBorderIcon from '@mui/icons-material/StarBorder';

// constantes
import { productInit, variantInit } from '../../../../constants/constants';

const CreateButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#465fff',
  textTransform: 'none',
  height: '45px',
  '&:hover': {
    backgroundColor: '#2e47e6',
  },
}));

export const NewProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftId = searchParams.get('draft_id') || '';
  const skipPromptRef = useRef(false);
  // const matches = useMediaQuery('(max-width:600px)');
  // estado UI de módulo
  const { loading2 } = useSelector(state => state.ui);

  const { products, snackbar } = useSelector(state => state.product);
  const { open, message } = snackbar;
  // valor de producto y variantes
  const [prodValues, setProdValuesState] = useState(productInit);
  const [dirty, setDirty] = useState(false);
  const setProdValues = value => {
    setDirty(true);
    setProdValuesState(value);
  };
  const {
    title, info, additional_images, featured,
    refrigerated, medicine, keywords, variants,
    brand
  } = prodValues;
  // contador (identificador de variante - key)
  const [counter, setCounter] = useState(1);
  // variantes como producto único (se utiliza en ProductFBT.js)
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    dispatch(loadProductList()); // productos
    dispatch(categoriesLoad()); // categorías y marcas de producto
  }, []);

  useEffect(() => {
    if (!draftId)
      return;

    skipPromptRef.current = false;
    let mounted = true;
    dispatch(uiLoading2(true));
    getProductDraft(draftId)
      .then(body => {
        if (!mounted)
          return;
        if (!body.status) {
          swalMsg('warning', 'Borrador', 'No se encontró el borrador solicitado');
          navigate('/nefro-admin/productos', { replace: true });
          return;
        }
        const draft = body.resp.draft;
        setProdValuesState({
          ...productInit,
          ...draft,
          additional_images: draft.additional_images || [],
          variants: (draft.variants || []).map((variant, index) => ({
            ...variantInit,
            ...variant,
            key: String(variant.draft_variant_id || index)
          }))
        });
        setCounter((draft.variants?.length || 0) + 1);
        setDirty(false);
      })
      .catch(() => swalMsg(
        'error',
        'Borrador',
        'No fue posible cargar el borrador'
      ))
      .finally(() => dispatch(uiLoading2(false)));

    return () => {
      mounted = false;
    };
  }, [dispatch, draftId, navigate]);

  useEffect(() => {
    const warnBeforeExit = event => {
      if (!dirty)
        return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeExit);
    return () => window.removeEventListener('beforeunload', warnBeforeExit);
  }, [dirty]);

  // producto agregado (redireccionar a lista de productos)
  useEffect(() => {
    // al cargar los productos, se toma cada variante como único
    if (products.length > 0) {
      let list_items = [];
      for (const i of products) { // productos
        if (i.catalog_status === 'archived')
          continue;
        const { _id: prod_id, title, prod_variants } = i;
        for (const j of prod_variants) { // variantes
          const { _id: prod_var_id, variant_title } = j;
          list_items.push({
            prod_id,
            prod_var_id,
            checked: (prodValues.fbt_item || []).some(item =>
              String(item.prod_var_id) === String(prod_var_id)
            ),
            title: getTitle(title, variant_title)
          });
        };
      };
      setAllProducts(list_items);
    }
  }, [products, prodValues.fbt_item]);

  // título / información
  const handleInputChange = ({ target }) => {
    const name = target.name;
    const value = target.value;
    if (name === 'title') {
      const _url = generateURL(value);
      setProdValues({
        ...prodValues,
        title: value,
        url: _url
      });
    } else {
      setProdValues({
        ...prodValues,
        [name]: value
      });
    }
  };

  // producto destacado / necesita refrigeración
  const handleToggleChange = ({ target }) => {
    const _name = target.name;
    if (_name === 'featured')
      setProdValues({
        ...prodValues,
        featured: !featured
      });
    else if (_name === 'refrigerated')
      setProdValues({
        ...prodValues,
        refrigerated: target.checked
      });
    else if (_name === 'medicine')
      setProdValues({
        ...prodValues,
        medicine: target.checked
      });
  };

  // añadir nueva variante
  const addVariant = () => {
    setProdValues({
      ...prodValues,
      variants: [...variants, {
        ...variantInit,
        key: counter
      }]
    });
    // variante añadida
    setCounter(counter + 1);
  };

  // registrar producto y variantes
  const handleSubmit = (e) => {
    e.preventDefault();
    if (form1Validator(prodValues)) { // registrar producto
      const fbt_item = allProducts.filter(e => e.checked).map(u => {
        const { prod_id, prod_var_id } = u;
        return { prod_id, prod_var_id };
      });
      if (!draftId) {
        dispatch(addProduct(
          { ...prodValues, fbt_item },
          destination => {
            skipPromptRef.current = true;
            navigate(destination);
          }
        ));
        return;
      }

      dispatch(uiLoading2(true));
      saveProductDraft(prodValues, fbt_item, draftId)
        .then(saved => {
          if (!saved.status)
            return saved;
          return publishProductDraft(saved.resp.draft._id);
        })
        .then(body => {
          if (!body?.status) {
            const details = body?.resp?.errors?.join(' ') ||
              body?.resp?.message ||
              'No fue posible publicar el borrador.';
            swalMsg('warning', 'Publicar borrador', details);
            return;
          }
          setDirty(false);
          skipPromptRef.current = true;
          posMsg('success', 'Producto publicado');
          navigate('/nefro-admin/productos');
        })
        .catch(() => swalMsg(
          'error',
          'Publicar borrador',
          'Ocurrió un incidente al publicar el producto'
        ))
        .finally(() => dispatch(uiLoading2(false)));
    }
  };

  const handleSaveDraft = async() => {
    const fbt_item = allProducts.filter(item => item.checked).map(item => ({
      prod_id: item.prod_id,
      prod_var_id: item.prod_var_id
    }));
    dispatch(uiLoading2(true));
    try {
      const body = await saveProductDraft(prodValues, fbt_item, draftId);
      if (!body.status) {
        const savedDraftId = body.resp?.draft?._id;
        if (!draftId && savedDraftId) {
          skipPromptRef.current = true;
          setSearchParams(
            { draft_id: savedDraftId },
            { replace: true }
          );
        }
        const messages = {
          'empty-draft': 'Captura al menos un dato o agrega una imagen.',
          'drive-unavailable': 'No fue posible guardar las imágenes en Drive.'
        };
        swalMsg(
          'warning',
          'Guardar borrador',
          messages[body.msg] || 'No fue posible guardar el borrador.'
        );
        return false;
      }
      const draft = body.resp.draft;
      setProdValuesState({
        ...productInit,
        ...draft,
        additional_images: draft.additional_images || [],
        variants: (draft.variants || []).map((variant, index) => ({
          ...variantInit,
          ...variant,
          key: String(variant.draft_variant_id || index)
        }))
      });
      setCounter((draft.variants?.length || 0) + 1);
      setDirty(false);
      if (!draftId) {
        skipPromptRef.current = true;
        setSearchParams(
          { draft_id: draft._id },
          { replace: true }
        );
      }
      posMsg('success', 'Borrador guardado por 30 días');
      return true;
    } catch {
      swalMsg('error', 'Guardar borrador', 'No fue posible guardar el borrador');
      return false;
    } finally {
      dispatch(uiLoading2(false));
    }
  };

  useProductExitGuard({
    dirty,
    skipPromptRef,
    saveDraft: handleSaveDraft
  });

  const goToProductList = () => {
    navigate('/nefro-admin/productos');
  };

  const handleDeleteRemoteImage = async fileId => {
    if (!draftId)
      return true;
    try {
      const body = await deleteProductDraftImage(draftId, fileId);
      if (body.status)
        return true;
      swalMsg('warning', 'Imagen', 'No fue posible eliminar la imagen');
      return false;
    } catch (err) {
      swalMsg('warning', 'Imagen', 'No fue posible eliminar la imagen');
      return false;
    }
  };

  // cerrar snackbar
  const handleSnackbarClose = () => {
    dispatch(productSnackbarClose());
  };

  const snackbarAction = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        onClick={handleSnackbarClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <Container maxWidth='xxl' sx={{ marginTop: '90px', mb: 5 }}>
      <Typography variant='h5' sx={{ fontWeight: '600' }} paragraph>
        {draftId ? 'Continuar borrador de producto' : 'Añadir nuevo producto'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
        <Button
          onClick={goToProductList}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Volver a productos
        </Button>
        <CreateButton variant='contained' onClick={handleSubmit}>
          {draftId ? 'Publicar producto' : 'Crear producto'}
        </CreateButton>
        <Button variant="outlined" onClick={handleSaveDraft}>
          Guardar borrador
        </Button>
      </Box>
      <Box sx={{ border: '1px solid #e4e7ec', borderRadius: '8px', boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)', mb: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, alignItems: 'center', p: 2 }}>
          <Typography variant='body1' sx={{ fontWeight: '600', fontSize: '18px' }}>Detalles del producto</Typography>
          {/* Productos destacado */}
          <ToggleButton
            size='small'
            name="featured"
            value="check"
            selected={featured}
            onChange={handleToggleChange}>
            {
              featured ? <>Destacado<StarIcon sx={{ color: '#ffd600', ml: 1.5 }} /></> :
                <>No destacado<StarBorderIcon sx={{ ml: 1.5 }} /></>
            }
          </ToggleButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={4}>
            {/* Nombre del producto */}
            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: '600', color: '#707b7c' }} gutterBottom>Nombre del producto:</Typography>
              <TextField
                fullWidth
                name="title"
                placeholder="Título"
                variant="outlined"
                value={title}
                onChange={handleInputChange}
                sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* necesita refrigeración */}
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      name="refrigerated"
                      checked={refrigerated}
                      onChange={handleToggleChange} />}
                  label="Necesita refrigeración" />
              </FormGroup>

              {/* es medicamento */}
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      name="medicine"
                      checked={medicine}
                      onChange={handleToggleChange} />}
                  label="Es medicamento" />
              </FormGroup>
            </Grid>

            {/* Información adicional */}
            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: '600', color: '#707b7c' }} gutterBottom>Información adicional:</Typography>
              <TextField
                fullWidth
                name="info"
                placeholder="Información adicional"
                variant="outlined"
                multiline
                rows={7.7}
                value={info}
                onChange={handleInputChange}
                sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, }}
              />
            </Grid>

            {/* Comprados juntos frecuentemente */}
            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: '600', color: '#707b7c' }} gutterBottom>Comprados juntos frecuentemente:</Typography>
              <ProductFBT
                allProducts={allProducts}
                setAllProducts={setAllProducts} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: '600', color: '#707b7c' }} gutterBottom>Keywords:</Typography>
              <ProductKeywords
                keywords={keywords}
                setProdValues={setProdValues}
              />
            </Grid>

            {/* Imágenes adicionales */}
            <Grid item xs={12}>
              <Typography variant='body2' sx={{ fontWeight: '600', color: '#707b7c' }} paragraph>Imágenes adicionales:</Typography>
              <ImagesContainer
                images={additional_images}
                setProdValues={setProdValues}
                onDeleteRemote={handleDeleteRemoteImage}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Grid container spacing={5}>
        <Grid item xl={7} lg={7} md={6} sm={12} xs={12}>
          {/* Variantes */}
          <Box>
            {
              variants.map((variant, i) =>
                <NewVariant
                  key={variant.key}
                  pos={i}
                  title={title}
                  variant={variant}
                  setProdValues={setProdValues}
                  onDeleteRemote={handleDeleteRemoteImage} />
              )
            }
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                endIcon={<AddBoxIcon />}
                onClick={addVariant}>
                Nueva variante
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xl={5} lg={5} md={6} sm={12} xs={12}>
          {/* Categoría del producto */}
          <Box sx={{ mb: 3 }}>
            <CategorySelect
              setProdValues={setProdValues}
              prodCategory={prodValues.prod_category}
            />
          </Box>

          {/* Marca del producto */}
          <ProductBrand
            brand={brand}
            setProdValues={setProdValues}
          />
        </Grid>

        <Grid item xs={12} sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mb: 3 }}>
            <Button
              onClick={goToProductList}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
            >
              Volver a productos
            </Button>
            <Button variant="outlined" onClick={handleSaveDraft}>
              Guardar borrador
            </Button>
            <CreateButton variant='contained' onClick={handleSubmit}>
              {draftId ? 'Publicar producto' : 'Crear producto'}
            </CreateButton>
          </Box>
        </Grid>

        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={message}
          action={snackbarAction} />
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading2}>
          <CircularProgress />
        </Backdrop>

        {/* <Fab type="submit"
          color="primary"
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2)
          }}>
          <i className="fa-solid fa-floppy-disk" style={{ fontSize: '1.2rem' }}></i>
        </Fab> */}
      </Grid>
    </Container>
  );
};

export default NewProduct;
